// Helper reusável de drag-and-drop via PointerEvents (suporta mouse + touch).
// Padrão: ghost (clone do elemento) segue o pointer; original fica visível mas opaco.
// Detecção de target via document.elementFromPoint + dataset.
//
// Uso típico (numa activity):
//
//   makeDraggable(sourceEl, {
//     getDropTarget: (x, y) => document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-drop-target]'),
//     onDrop: (targetEl) => { ... lógica da activity ... },
//     onTap: () => { ... clique sem arrastar — mantém click-and-click ... },
//   });

export interface DraggableOptions {
  // Retorna o elemento-alvo embaixo do pointer, ou null se nenhum.
  getDropTarget: (clientX: number, clientY: number) => HTMLElement | null;
  // Chamado quando o aluno solta sobre um alvo válido.
  onDrop: (targetEl: HTMLElement) => void;
  // Chamado quando o aluno solta fora de qualquer alvo. Opcional.
  onDropOutside?: () => void;
  // Chamado se o pointer foi pressionado e solto SEM mover (= tap). Mantém click-and-click.
  onTap?: () => void;
  // CSS class adicionada ao elemento sendo arrastado (default: 'is-dragging').
  draggingClass?: string;
  // CSS class adicionada ao alvo quando o pointer está sobre ele.
  hoverClass?: string;
  // Distância em px que o pointer precisa percorrer pra ser considerado "drag" e não "tap".
  dragThreshold?: number;
  // Permite desativar globalmente (ex: enquanto valida resposta).
  isEnabled?: () => boolean;
}

const DEFAULT_THRESHOLD = 6;
const DEFAULT_DRAGGING_CLASS = 'is-dragging';
const DEFAULT_HOVER_CLASS = 'is-drop-hover';

export function makeDraggable(el: HTMLElement, options: DraggableOptions): () => void {
  let active = false;
  let startX = 0;
  let startY = 0;
  let pointerId = -1;
  let ghost: HTMLElement | null = null;
  let lastHover: HTMLElement | null = null;
  let moved = false;
  const draggingClass = options.draggingClass ?? DEFAULT_DRAGGING_CLASS;
  const hoverClass = options.hoverClass ?? DEFAULT_HOVER_CLASS;
  const threshold = options.dragThreshold ?? DEFAULT_THRESHOLD;

  const setHover = (target: HTMLElement | null): void => {
    if (lastHover === target) return;
    if (lastHover) lastHover.classList.remove(hoverClass);
    if (target) target.classList.add(hoverClass);
    lastHover = target;
  };

  const createGhost = (): HTMLElement => {
    const clone = el.cloneNode(true) as HTMLElement;
    const rect = el.getBoundingClientRect();
    clone.style.position = 'fixed';
    clone.style.left = `${rect.left}px`;
    clone.style.top = `${rect.top}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.margin = '0';
    clone.style.pointerEvents = 'none';
    clone.style.zIndex = '9999';
    clone.style.opacity = '0.9';
    clone.style.transform = 'scale(1.08)';
    clone.style.transition = 'transform 120ms ease';
    clone.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.25)';
    clone.classList.add('drag-ghost');
    document.body.appendChild(clone);
    return clone;
  };

  const moveGhost = (clientX: number, clientY: number): void => {
    if (!ghost) return;
    const rect = el.getBoundingClientRect();
    ghost.style.left = `${clientX - rect.width / 2}px`;
    ghost.style.top = `${clientY - rect.height / 2}px`;
  };

  const cleanup = (): void => {
    active = false;
    moved = false;
    setHover(null);
    el.classList.remove(draggingClass);
    if (ghost) {
      ghost.remove();
      ghost = null;
    }
    try { el.releasePointerCapture(pointerId); } catch { /* no-op */ }
  };

  const onPointerDown = (e: PointerEvent): void => {
    if (options.isEnabled && !options.isEnabled()) return;
    if (e.button !== undefined && e.button !== 0) return;
    active = true;
    moved = false;
    startX = e.clientX;
    startY = e.clientY;
    pointerId = e.pointerId;
    try { el.setPointerCapture(pointerId); } catch { /* no-op */ }
    e.preventDefault();
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (!active) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (!moved && Math.hypot(dx, dy) >= threshold) {
      moved = true;
      ghost = createGhost();
      el.classList.add(draggingClass);
    }
    if (!moved) return;
    moveGhost(e.clientX, e.clientY);
    const target = options.getDropTarget(e.clientX, e.clientY);
    setHover(target);
  };

  const onPointerUp = (e: PointerEvent): void => {
    if (!active) return;
    if (!moved) {
      cleanup();
      options.onTap?.();
      return;
    }
    const target = options.getDropTarget(e.clientX, e.clientY);
    if (target) {
      options.onDrop(target);
    } else {
      options.onDropOutside?.();
    }
    cleanup();
  };

  const onPointerCancel = (): void => {
    if (!active) return;
    options.onDropOutside?.();
    cleanup();
  };

  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerup', onPointerUp);
  el.addEventListener('pointercancel', onPointerCancel);

  // touch-action manipulation pra evitar scroll do navegador durante drag
  if (!el.style.touchAction) el.style.touchAction = 'none';

  return (): void => {
    el.removeEventListener('pointerdown', onPointerDown);
    el.removeEventListener('pointermove', onPointerMove);
    el.removeEventListener('pointerup', onPointerUp);
    el.removeEventListener('pointercancel', onPointerCancel);
    cleanup();
  };
}
